import React, { useState } from "react";
import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from "@mui/material";
import { useTranslate, useNotification, useNavigation } from "@refinedev/core";
import axiosInstance from "../../api/axios-config";

const PROTOCOL_TYPES = ["HTTP", "TCP_SOCKET", "NONE"] as const;
const MESSAGE_FORMATS = ["JSON", "SIA_DC09", "NONE"] as const;

export const ProviderCreate: React.FC = () => {
  const translate = useTranslate();
  const { open } = useNotification();
  const { list } = useNavigation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [protocolType, setProtocolType] = useState<string>("HTTP");
  const [messageFormat, setMessageFormat] = useState<string>("JSON");
  const [configJson, setConfigJson] = useState("");

  const handleSubmit = async () => {
    if (!code.trim() || !name.trim()) {
      open?.({ type: "error", message: "Code et nom sont requis" });
      return;
    }

    let config: Record<string, any> | undefined;
    if (configJson.trim()) {
      try {
        config = JSON.parse(configJson);
      } catch {
        open?.({ type: "error", message: "Config JSON invalide" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.post("/monitoring/providers", {
        code: code.trim(),
        name: name.trim(),
        protocolType,
        messageFormat,
        config,
      });
      open?.({ type: "success", message: "Provider créé avec succès" });
      list("telesurveilleur");
    } catch (error: any) {
      open?.({
        type: "error",
        message: error.response?.data?.message || "Erreur lors de la création",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Create
      title="Créer un provider"
      isLoading={isSubmitting}
      saveButtonProps={{ onClick: handleSubmit, disabled: isSubmitting }}
    >
      <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            fullWidth
            placeholder="ACME"
            helperText="Identifiant unique en majuscules"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Nom"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            placeholder="ACME Télésurveillance"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Protocole</InputLabel>
            <Select
              value={protocolType}
              onChange={(e) => setProtocolType(e.target.value)}
              label="Protocole"
            >
              {PROTOCOL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Format de message</InputLabel>
            <Select
              value={messageFormat}
              onChange={(e) => setMessageFormat(e.target.value)}
              label="Format de message"
            >
              {MESSAGE_FORMATS.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TextField
          label="Configuration (JSON)"
          value={configJson}
          onChange={(e) => setConfigJson(e.target.value)}
          multiline
          rows={4}
          fullWidth
          placeholder='{ "apiUrl": "https://...", "apiKey": "..." }'
          helperText="Configuration spécifique au provider (optionnel)"
          InputLabelProps={{ shrink: true }}
        />
      </Box>
    </Create>
  );
};
